import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "../lib/stripe_loader"

// The donate flow's Payment step (TMS-1807). Stripe's Payment Element takes the card (and the
// Express Checkout Element Apple Pay or Google Pay, where the device has them), in deferred mode:
// nothing is charged here. Pressing Donate, or approving in the wallet, turns the card into a
// ConfirmationToken and posts it; the server charges it once (Donations::PlaceGiftService).
// The Donate button is held while that happens, so it can't be pressed twice.
export default class extends Controller {
  static targets = ["card", "express", "expressSection", "name", "token", "submit", "error", "savedChoice", "newCard"]
  static values = {
    publishableKey: String,
    amount: Number,
    currency: { type: String, default: "usd" },
    recurring: Boolean,
    defaultName: String
  }

  async connect() {
    this.syncCard()
    if (!this.publishableKeyValue) return

    try {
      this.stripe = await loadStripe(this.publishableKeyValue)
      this.elements = this.stripe.elements({
        mode: this.recurringValue ? "subscription" : "payment",
        amount: this.amountValue,
        currency: this.currencyValue,
        paymentMethodTypes: ["card"],
        setupFutureUsage: this.recurringValue ? "off_session" : undefined,
        appearance: { variables: { colorText: "#1A1C43", colorDanger: "#EF4444" } }
      })
      this.mountCard()
      this.mountExpress()
    } catch (error) {
      console.error("Stripe initialization error:", error)
      this.showError("The payment form didn't load. Please refresh the page and try again.")
    }
  }

  mountCard() {
    if (!this.hasCardTarget) return

    // The cardholder's name comes from its own field, as in the design.
    this.cardElement = this.elements.create("payment", { fields: { billingDetails: { name: "never" } } })
    this.cardElement.mount(this.cardTarget)
  }

  mountExpress() {
    if (!this.hasExpressTarget) return

    const express = this.elements.create("expressCheckout", {
      buttonType: { applePay: "donate", googlePay: "donate" },
      paymentMethods: { applePay: "always", googlePay: "always", link: "never", amazonPay: "never", paypal: "never" }
    })
    express.on("ready", ({ availablePaymentMethods }) => {
      if (this.hasExpressSectionTarget) this.expressSectionTarget.hidden = !availablePaymentMethods
    })
    express.on("confirm", () => this.pay({ wallet: true }))
    express.mount(this.expressTarget)
  }

  // A signed-in donor's saved card (TMS-1808), or the form for a new one.
  syncCard() {
    if (!this.hasNewCardTarget || !this.hasSavedChoiceTarget) return

    const saved = this.savedChoiceTargets.find((input) => input.checked)?.value
    this.newCardTarget.hidden = Boolean(saved)
  }

  async submit(event) {
    // A saved card (TMS-1808) needs no token: the form goes as it is.
    if (this.element.querySelector("input[name='saved_card']:checked:not([value=''])")) {
      this.hold()
      return
    }

    event.preventDefault()
    await this.pay({ wallet: false })
  }

  async pay({ wallet }) {
    if (!this.elements) return
    this.hold()
    this.showError("")

    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) throw submitError

      const name = this.hasNameTarget ? this.nameTarget.value.trim() : ""
      const params = wallet ? {} : { payment_method_data: { billing_details: { name: name || this.defaultNameValue } } }
      const { error, confirmationToken } = await this.stripe.createConfirmationToken({ elements: this.elements, params })
      if (error) throw error

      this.tokenTarget.value = confirmationToken.id
      this.element.submit()
    } catch (error) {
      this.showError(error.message || "Your card details couldn't be checked. Please try again.")
      this.release()
    }
  }

  hold() {
    this.submitTargets.forEach((button) => {
      button.disabled = true
      button.dataset.label ||= button.textContent
      button.textContent = "Processing…"
    })
  }

  release() {
    this.submitTargets.forEach((button) => {
      button.disabled = false
      if (button.dataset.label) button.textContent = button.dataset.label
    })
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }
}
